import {useMutation} from '@tanstack/react-query';
import {createFileRoute} from '@tanstack/react-router';
import {useState} from 'react';
import {getEquipment, getEquipmentReport} from '@services/hub.ts';
import {VehicleReportResource} from '../../../types/appTypes';
import {errorMessage} from '@utils/toast.tsx';
import {CreateInput} from './components/CreateInput.tsx';
import {Disclaimer} from './components/Disclaimer.tsx';
import {EquipmentCreditsStatus} from './components/EquipmentCreditsStatus.tsx';
import {EquipmentPageHeader} from './components/EquipmentPageHeader.tsx';
import {List} from './components/List.tsx';
import {Overview} from './components/Overview.tsx';
import {PreviousLookups} from './components/PreviousLookups.tsx';
import {Skeleton} from './components/Skeleton.tsx';

type EquipmentCreditsStatusData = {
    clipsLeft: number;
}

export const Route = createFileRoute('/_protected/_equipment/equipment/')({
    head: () => ({
        meta: [{ title: `Udstyrsliste` }],
    }),
    component: CarDataComponentOuter
})

function CarDataComponentOuter() {
    const [query, setQuery] = useState('');
    const [equipmentReport, setEquipmentReport] = useState<VehicleReportResource | null>(null);
    const [hasLoadedEquipment, setHasLoadedEquipment] = useState(false);
    const [equipmentCreditsStatus, setEquipmentCreditsStatus] = useState<EquipmentCreditsStatusData | null>(null);

    const createEquipmentMutation = useMutation({
        mutationFn: (vin: string) => getEquipment(vin),
        onSuccess: (response) => {
            const report = response.data.data;
            const clipAmountHeader = response.headers['x-clipamount'];
            const creditsLeftHeader = response.headers['x-creditsleft'];
            const clipAmount = Number(clipAmountHeader);
            const creditsLeft = Number(creditsLeftHeader);

            if (!Number.isNaN(clipAmount) && !Number.isNaN(creditsLeft)) {
                setEquipmentCreditsStatus({
                    clipsLeft: clipAmount + creditsLeft,
                });
            }

            setEquipmentReport(report);
            setQuery(report.vin);
            setHasLoadedEquipment(true);
        },
        onError: (error) => {
            errorMessage(error);
        },
    });
    const getEquipmentReportMutation = useMutation({
        mutationFn: (id: string) => getEquipmentReport(id),
        onSuccess: (response) => {
            const report = response.data.data;
            setEquipmentReport(report);
            setQuery(report.vin);
            setHasLoadedEquipment(true);
        },
        onError: (error) => {
            errorMessage(error);
        },
    });
    const isLoadingEquipment = createEquipmentMutation.isPending || getEquipmentReportMutation.isPending;
    const equipmentList = equipmentReport?.equipment ?? null;
    const reportLink = equipmentReport?.links.report ?? null;

    function handleSetQuery(value: string) {
        setQuery(value)
        setHasLoadedEquipment(false)
    }

    return (
        <div className='p-6 space-y-5'>
            <EquipmentPageHeader/>
            <div className="grid gap-6 grid-cols-1 items-start lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className='space-y-6'>
                    <CreateInput
                        isPending={isLoadingEquipment}
                        query={query}
                        showReset={hasLoadedEquipment && query.length > 0}
                        hasLoadedEquipment={hasLoadedEquipment}
                        onQueryChange={handleSetQuery}
                        onGetEquipment={(vin) => {
                            setEquipmentReport(null);
                            setEquipmentCreditsStatus(null);
                            createEquipmentMutation.mutate(vin);
                        }}
                        onReset={() => {
                            setQuery('');
                            setEquipmentReport(null);
                            setHasLoadedEquipment(false);
                            setEquipmentCreditsStatus(null);
                        }}
                    />
                    {equipmentCreditsStatus && (
                        <EquipmentCreditsStatus
                            clipsLeft={equipmentCreditsStatus.clipsLeft}
                        />
                    )}
                    {isLoadingEquipment && <Skeleton variant="overview"/>}
                    {equipmentReport && <Overview equipmentReport={equipmentReport}/>}
                    {isLoadingEquipment && <Skeleton variant="list"/>}
                    {equipmentList && (
                        <>
                            <List equipment={equipmentList} reportLink={reportLink} vin={equipmentReport?.vin}/>
                            <Disclaimer/>
                        </>
                    )}
                </div>
                <PreviousLookups
                    isLoadingLookup={isLoadingEquipment}
                    onSelectLookup={(id) => {
                        setEquipmentReport(null);
                        setEquipmentCreditsStatus(null);
                        getEquipmentReportMutation.mutate(id);
                    }}
                />
            </div>
        </div>
    );
}
